import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Zap, Clock, CreditCard, BarChart3, QrCode, Users, Shield, Headphones,
  Bike, Footprints, Medal, Car, Mountain, Target, Wind, Flag, CircleDot,
  Dumbbell, Swords, ChevronRight, Check, ArrowRight, Globe, Timer, ShoppingBag, Handshake
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTranslation } from 'react-i18next';
import PlatformSlideshow from '../components/PlatformSlideshow';

const featureIcons = [Clock, QrCode, BarChart3, CreditCard, Timer, Shield, ShoppingBag, Handshake];

const sportsList = [
  { key: 'running', icon: Footprints },
  { key: 'cycling', icon: Bike },
  { key: 'triathlon', icon: Medal },
  { key: 'vtt', icon: Mountain },
  { key: 'crossfit', icon: Dumbbell },
  { key: 'combat', icon: Swords },
  { key: 'rallye', icon: Car },
  { key: 'kitesurf', icon: Wind },
  { key: 'golf', icon: Flag },
  { key: 'archery', icon: Target },
  { key: 'petanque', icon: CircleDot },
  { key: 'andMore', icon: Globe }
];

const OrgaLandingPage = () => {
  const { t } = useTranslation();

  const features = featureIcons.map((icon, idx) => ({
    icon,
    title: t(`orga.feat${idx + 1}Title`),
    desc: t(`orga.feat${idx + 1}Desc`)
  }));

  const stats = [
    { value: t('orga.stat1Value'), label: t('orga.stat1Label') },
    { value: t('orga.stat2Value'), label: t('orga.stat2Label') },
    { value: t('orga.stat3Value'), label: t('orga.stat3Label') },
    { value: t('orga.stat4Value'), label: t('orga.stat4Label') }
  ];

  const advantages = Array.from({ length: 16 }, (_, i) => t(`orga.adv${i + 1}`));

  const sports = sportsList.map(s => ({ name: t(`sports.${s.key}`), icon: s.icon }));

  const ecosystem = [
    { num: '01', title: t('orga.eco1Title'), desc: t('orga.eco1Desc') },
    { num: '02', title: t('orga.eco2Title'), desc: t('orga.eco2Desc') },
    { num: '03', title: t('orga.eco3Title'), desc: t('orga.eco3Desc') },
    { num: '04', title: t('orga.eco4Title'), desc: t('orga.eco4Desc') }
  ];
  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Hero */}
      <section className="relative bg-asphalt text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-brand rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-brand rounded-full translate-x-1/2 translate-y-1/2 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 relative">
          <div className="max-w-3xl">
            <motion.span
              className="inline-block bg-brand text-white font-heading font-bold uppercase tracking-widest text-xs px-4 py-1.5 mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              {t('orga.badge')}
            </motion.span>
            <motion.h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              {t('orga.heroTitle')}
            </motion.h1>
            <motion.p
              className="text-lg text-slate-300 mt-6 max-w-xl leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {t('orga.heroDesc')}
            </motion.p>
            <motion.div
              className="flex flex-wrap gap-4 mt-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Link to="/register?role=organizer">
                <Button className="btn-primary h-14 px-8 text-base gap-2" data-testid="orga-cta-register">
                  {t('orga.heroCta')} <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
            <motion.div
              className="flex items-center gap-8 mt-10 text-sm text-slate-400"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-brand" /> {t('orga.heroCheck1')}</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-brand" /> {t('orga.heroCheck2')}</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-brand" /> {t('orga.heroCheck3')}</span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-brand text-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="font-heading text-4xl font-bold">{s.value}</div>
                <div className="text-sm text-white/80 mt-1">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24" data-testid="orga-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-brand font-heading font-bold uppercase tracking-widest text-sm">{t('orga.techLabel')}</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase mt-2">
              {t('orga.techTitle')}
            </h2>
            <p className="text-slate-500 mt-4 max-w-2xl mx-auto">
              {t('orga.techDesc')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, idx) => (
              <motion.div
                key={f.title}
                className="p-6 border border-slate-200 hover:border-brand group transition-all"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.08 }}
                viewport={{ once: true }}
                whileHover={{ y: -4 }}
              >
                <f.icon className="w-10 h-10 text-brand mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="font-heading font-bold text-lg uppercase tracking-wider mb-2">{f.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Slideshow */}
      <PlatformSlideshow />

      {/* Advantages Checklist */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-brand font-heading font-bold uppercase tracking-widest text-sm">{t('orga.allIncludedLabel')}</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase mt-2">
                {t('orga.allIncludedTitle')}
              </h2>
              <p className="text-slate-500 mt-4 leading-relaxed">
                {t('orga.allIncludedDesc')}
              </p>
              <div className="mt-8">
                <Link to="/register?role=organizer">
                  <Button className="btn-primary gap-2" data-testid="orga-cta-advantages">
                    {t('orga.startFree')} <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {advantages.map((a, idx) => (
                <motion.div
                  key={a}
                  className="flex items-center gap-2 p-3 bg-white border border-slate-200"
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: idx * 0.03 }}
                  viewport={{ once: true }}
                >
                  <Check className="w-4 h-4 text-brand flex-shrink-0" />
                  <span className="text-sm font-medium">{a}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Sports */}
      <section className="py-24" data-testid="orga-sports">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-brand font-heading font-bold uppercase tracking-widest text-sm">{t('orga.multisportLabel')}</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase mt-2">
              {t('orga.multisportTitle')}
            </h2>
            <p className="text-slate-500 mt-4 max-w-2xl mx-auto">
              {t('orga.multisportDesc')}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {sports.map((s, idx) => (
              <motion.div
                key={s.name}
                className="p-5 border border-slate-200 text-center hover:border-brand group transition-all"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: idx * 0.05 }}
                viewport={{ once: true }}
                whileHover={{ y: -3 }}
              >
                <s.icon className="w-8 h-8 mx-auto mb-2 text-brand group-hover:scale-110 transition-transform" />
                <h3 className="font-heading font-bold uppercase tracking-wider text-[10px]">{s.name}</h3>
              </motion.div>
            ))}
          </div>
          <p className="text-center text-slate-500 text-sm mt-6">
            {t('orga.multisportFooter')}
          </p>
        </div>
      </section>

      {/* Ecosystem */}
      <section className="py-24 bg-asphalt text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-brand font-heading font-bold uppercase tracking-widest text-sm">{t('orga.ecoLabel')}</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase mt-2">
              {t('orga.ecoTitle')}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {ecosystem.map((item, idx) => (
              <motion.div
                key={item.num}
                className="border border-slate-700 p-8"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <span className="text-brand font-heading text-4xl font-bold">{item.num}</span>
                <h3 className="font-heading font-bold text-xl uppercase mt-4 mb-3">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Support */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-brand font-heading font-bold uppercase tracking-widest text-sm">{t('orga.supportLabel')}</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase mt-2">
                {t('orga.supportTitle')}
              </h2>
              <p className="text-slate-500 mt-4 leading-relaxed">
                {t('orga.supportDesc')}
              </p>
            </div>
            <div className="space-y-4">
              <div className="p-6 border border-slate-200 border-l-4 border-l-brand">
                <h3 className="font-heading font-bold uppercase tracking-wider mb-2">{t('orga.supportOrgTitle')}</h3>
                <p className="text-slate-500 text-sm">{t('orga.supportOrgDesc')}</p>
              </div>
              <div className="p-6 border border-slate-200 border-l-4 border-l-brand">
                <h3 className="font-heading font-bold uppercase tracking-wider mb-2">{t('orga.supportPartTitle')}</h3>
                <p className="text-slate-500 text-sm">{t('orga.supportPartDesc')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-brand text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2
            className="font-heading text-3xl md:text-5xl font-bold uppercase"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t('orga.ctaTitle')}
          </motion.h2>
          <motion.p
            className="text-lg text-white/80 mt-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            viewport={{ once: true }}
          >
            {t('orga.ctaDesc')}
          </motion.p>
          <motion.div
            className="mt-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
          >
            <Link to="/register?role=organizer">
              <Button className="bg-white text-brand hover:bg-slate-100 h-14 px-8 text-base font-heading font-bold uppercase tracking-wider gap-2" data-testid="orga-cta-final">
                {t('orga.ctaButton')} <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <p className="text-sm text-white/60 mt-4">{t('orga.ctaFooter')}</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default OrgaLandingPage;
